import { AxiosResponse } from "axios";
import { BundleResultsGrid } from "../../../components/resultsGrid/BundleResultsGrid";
import { FluidVirtualGrid } from "../../../components/resultsGrid/FluidVirtualGrid";
import { sizeEstimator } from "../../../components/cards/hooks/useGridCardDimensions";
import { useTopBarHeight } from "../../../hooks/userTopBarHeight";
import {
  Section,
  LayoutStyle,
  ServerListResponse,
  Comic,
  Artist,
  Article,
  Release,
  Bundle,
} from "../../../types";

type ResultItem = Comic | Artist | Article | Release | Bundle;

interface ExpandedContentGridProps {
  section: Section;
  allPages: AxiosResponse<ServerListResponse<ResultItem>>[];
  hasNextPage: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  header: React.ReactNode;
  /** Callback for scroll restoration - reports first visible row index */
  onVisibleIndexChange?: (index: number) => void;
  /** Row index to restore scroll to on back navigation */
  restoreToIndex?: number;
}

export function ExpandedContentGrid({
  section,
  allPages,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  header,
  onVisibleIndexChange,
  restoreToIndex,
}: ExpandedContentGridProps) {
  const topBarHeight = useTopBarHeight();

  const originalContext = section.meta.layout.style.context;
  const originalLayout = section.meta.layout;

  // Bundles use BundleResultsGrid
  if (originalContext === "bundle") {
    return (
      <BundleResultsGrid
        header={header}
        gridHeightAdjustment={`${topBarHeight.get()}px`}
        layout={originalLayout}
        isLoading={false}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        allPages={allPages as any}
        columnPadding={16}
        gridKey={"bundle-expanded-content-grid"}
      />
    );
  }

  // Use FluidVirtualGrid with v2 card styles
  const v2Layout = {
    ...originalLayout,
    style: {
      ...originalLayout.style,
      mode: "grid" as LayoutStyle["mode"],
      size: "v2" as LayoutStyle["size"],
    },
  };
  const dims = sizeEstimator(
    v2Layout.style.context,
    v2Layout.style.mode,
    v2Layout.style.size,
  );
  return (
    <FluidVirtualGrid
      gridKey={`v2-expanded-${section.id}`}
      header={header}
      gridHeightAdjustment={`${topBarHeight.get()}px`}
      layout={v2Layout}
      minCardWidth={dims.width}
      minColumns={2}
      gap={24}
      isLoading={false}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
      hasNextPage={hasNextPage}
      allPages={allPages as any}
      onVisibleIndexChange={onVisibleIndexChange}
      restoreToIndex={restoreToIndex}
    />
  );
}
